import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, AlertCircle, Truck, RefreshCw, Ban, Phone, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contactInfo } from '../mock';

export const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="max-w-4xl mx-auto">
          <Button 
            onClick={() => navigate('/book')}
            variant="outline"
            className="mb-8 flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Booking</span>
          </Button>

          <Card className="shadow-xl border-0 bg-white">
            <CardContent className="p-8 sm:p-12">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold text-lg">
                    AIS
                  </div>
                  <h1 className="text-3xl font-bold text-slate-900">Refund / Re-Delivery Policy</h1>
                </div>
                <p className="text-slate-600">AIS Express Same-Day Delivery Services</p>
                <p className="text-sm text-slate-500 mt-2">Last Updated: 24/10/2025</p>
              </div>

              <div className="prose prose-slate max-w-none">

                {/* 1. Delivery Issues */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2 text-emerald-600" />
                    1. Delivery Issues
                  </h2>
                  <p className="text-slate-700 leading-relaxed">
                    If there is a problem with your delivery, please contact us within <strong>24 hours</strong> of the delivery attempt.
                  </p>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                    <p className="text-blue-800">
                      <strong>Important:</strong> Issues must be reported within 24 hours to be eligible for re-delivery or refund consideration.
                    </p>
                  </div>
                </section>

                {/* 2. Re-Delivery */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <RefreshCw className="h-5 w-5 mr-2 text-emerald-600" />
                    2. Re-Delivery
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    If the recipient is not available at the delivery address:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    <li>We may attempt re-delivery (additional charges may apply), or</li>
                    <li>Return the item to the sender.</li>
                  </ul>
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-4">
                    <p className="text-amber-800">
                      <strong>Note:</strong> Re-delivery fees cover additional driver time and travel costs.
                    </p>
                  </div>
                </section>

                {/* 3. Refunds */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Truck className="h-5 w-5 mr-2 text-emerald-600" />
                    3. Refunds
                  </h2>
                  
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">Refunds are only provided if:</h3>
                    <ul className="list-disc pl-6 space-y-2 text-slate-700">
                      <li>We cancel the delivery before dispatch, or</li>
                      <li>We are unable to complete the delivery for reasons within our control.</li>
                    </ul>
                    <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mt-4">
                      <p className="text-emerald-800 font-medium">
                        ✓ Eligible: Service cancelled by us or failure due to our error
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">Refunds are NOT provided for:</h3>
                    <ul className="list-disc pl-6 space-y-2 text-slate-700">
                      <li>Incorrect or incomplete addresses</li>
                      <li>Items prohibited, restricted, or unsafe to transport</li>
                      <li>Failure to collect or accept delivery</li>
                    </ul>
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 mt-4">
                      <p className="text-red-800 font-medium">
                        <Ban className="h-4 w-4 inline mr-2" />
                        Not Eligible: Customer-caused issues or prohibited items
                      </p>
                    </div>
                  </div>
                </section>

                {/* 4. Contact for Issues */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Phone className="h-5 w-5 mr-2 text-emerald-600" />
                    4. Contact for Issues
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    To request re-delivery or discuss an issue, contact:
                  </p>
                  <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Mail className="h-4 w-4 text-emerald-600" />
                      <span className="text-slate-700">Email: <a href={`mailto:${contactInfo.email}`} className="text-emerald-600 hover:text-emerald-700 font-medium">{contactInfo.email}</a></span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-emerald-600" />
                      <span className="text-slate-700">Phone: <a href={`tel:${contactInfo.phone}`} className="text-emerald-600 hover:text-emerald-700 font-medium">{contactInfo.phone}</a></span>
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg mt-4">
                    <p className="text-sm text-blue-800">
                      <strong>Best Practice:</strong> Have your tracking ID or quote reference ready when contacting us for faster assistance.
                    </p>
                  </div>
                </section>

              </div>

              {/* Agreement Footer */}
              <div className="mt-12 pt-8 border-t border-slate-200">
                <p className="text-sm text-slate-600 text-center">
                  By using AIS Express services, you acknowledge that you have read and understood this Refund / Re-Delivery Policy.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
